import { useParams } from "react-router-dom";
import { useGetOrderByIdQuery } from "@/features/organizations/api/ordersApi";
import PageLoading from "@/components/page-loading/PageLoading";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid2 as Grid,
  Paper,
  Stack,
  Divider,
  Button,
  Tabs,
  Tab,
  useTheme,
  Container,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import OrderDetailsTab from "@/components/OrderDetails/OrderDetails";
import OrderClientTab from "@/components/client-details/ClientDetails";
import ReceiptCustomer from "@/components/ReceiptCustomer.template";
import ReceiptKitchen from "@/components/ReceiptKitchen.template";
import { Order } from "@/types";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useFetchOrganizationsByIdQuery } from "@/features/organizations/api/organizationsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { setActiveOrganization } from "@/store/uiSlice";
import { EmptyState } from "@/components";
import Logo from "@/assets/logos/bitelink-logo-light.svg";
import { HugeiconsIcon } from "@hugeicons/react";
import { Invoice01Icon, Invoice02Icon } from "@hugeicons/core-free-icons";

type Tabs = Record<
  string,
  {
    label: string;
    component: ReactElement;
  }
>;

const INITIAL_TAB = "details";

const OrdersLinkPage = () => {
  const { orderId, organizationId } = useParams();
  const dispatch = useAppDispatch();
  const { palette } = useTheme();

  useEffect(() => {
    if (organizationId) {
      dispatch(setActiveOrganization(organizationId));
    }
  }, [organizationId, dispatch]);

  const { data: order, isLoading } = useGetOrderByIdQuery(
    { organizationId, orderId },
    { skip: !organizationId || !orderId }
  );

  useFetchOrganizationsByIdQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });

  const organization = useAppSelector(
    (state) => state?.organizations?.[organizationId]
  );

  // 🧾 PRINT
  const receiptCustomerRef = useRef<HTMLDivElement>(null);
  const receiptKitchenRef = useRef<HTMLDivElement>(null);

  const handlePrintCustomerReceipt = useReactToPrint({
    contentRef: receiptCustomerRef,
    documentTitle: "Cliente",
  });

  const handlePrintKitchenReceipt = useReactToPrint({
    contentRef: receiptKitchenRef,
    documentTitle: "Cocina",
  });

  const tabs = useMemo(() => {
    if (!order) return {};
    return {
      details: {
        label: "Pedido",
        component: <OrderDetailsTab {...(order as Order)} />,
      },
      clientInfo: {
        label: "Cliente",
        component: <OrderClientTab {...(order as Order)} />,
      },
    };
  }, [order]);

  const [activeTab, setActiveTab] =
    useState<keyof typeof tabs>(INITIAL_TAB);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: keyof typeof tabs
  ) => {
    setActiveTab(newValue);
  };

  if (isLoading) return <PageLoading />;

  if (!order || !activeTab) {
    return (
      <Stack
        spacing={2}
        bgcolor={"white"}
        height={"100%"}
        alignItems="center"
        justifyContent="center"
      >
        <EmptyState
          title="Pedido no disponible"
          description={`El pedido ${orderId} no fue encontrado`}
          action={{
            children: "Regresar",
            onClick: () =>
              window.location.replace(
                organization?.domain || "https://bitelink.website"
              ),
          }}
        />
      </Stack>
    );
  }

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: "#06175D", p: 0.5 }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <img src={Logo} alt="BiteLink" style={{ height: "43px" }} />
          </Stack>

        </Toolbar>
      </AppBar>

      <Box sx={{
        p: 2,
        bgcolor: palette.primary.light,
        minHeight: "100vh"
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={2} columnSpacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={2}>
                <Tabs value={activeTab} onChange={handleChange}>
                  {Object.entries(tabs).map(([tabKey, tab]) => (
                    <Tab key={tabKey} label={tab.label} value={tabKey} />
                  ))}
                </Tabs>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  {tabs[activeTab]?.component}
                </Box>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>

                {/* <Paper sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="body2">
                    Estado de la Orden
                  </Typography>
                  <Typography color="primary" fontWeight="bold">

                    Pendiente de Aceptación
                    <br />
                    {order.status}
                  </Typography>
                </Paper>


                <Paper sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="body2" mb={2}>
                    ACCIONES PRINCIPALES
                  </Typography>

                  <Stack spacing={2}>
                    <Button fullWidth variant="contained" color="success" startIcon={<HugeiconsIcon icon={CheckmarkCircle02Icon} />}>
                      Aceptar Orden
                    </Button>

                    <Button fullWidth variant="contained" color="error" startIcon={<HugeiconsIcon icon={CancelCircleIcon} />}>
                      Rechazar Orden
                    </Button>
                  </Stack>
                </Paper> */}

                <Paper sx={{ p: 2, borderRadius: 3 }}>
                  <Typography variant="body2" mb={2}>
                    DOCUMENTACIÓN
                  </Typography>

                  <Stack spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handlePrintKitchenReceipt}
                      startIcon={<HugeiconsIcon icon={Invoice01Icon} />}
                    >
                      Imprimir Comanda
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handlePrintCustomerReceipt}
                      startIcon={<HugeiconsIcon icon={Invoice02Icon} />}
                    >
                      Imprimir Factura
                    </Button>
                  </Stack>
                </Paper>

              </Stack>
            </Grid>
          </Grid>
        </Container>

        <div style={{ display: "none" }}>
          <div ref={receiptCustomerRef} style={{ height: "100%", width: "100%", display: "flex", flex: 1 }}>
            <ReceiptCustomer order={order as Order} />
          </div>
          <div ref={receiptKitchenRef} style={{ height: "100%", width: "100%", display: "flex", flex: 1 }}>
            <ReceiptKitchen order={order as Order} />
          </div>
        </div>
      </Box>
    </>
  );
};

export default OrdersLinkPage;